<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useClientsList } from '@/api'
import { EditFieldModal } from '@/components/CreatorDetail'
import { formatRevenueTierLabel } from '@/lib/adapters/clients.adapter'
import { debounce } from '@/lib/helpers'
import type { RevenueTierType } from '@/lib/types/common.types'
import { BoxComponent, SelectComponent } from '@/ui-library'

const props = defineProps<{
  clientId: number
  clientName: string
  revenueTierId: number
  revenueTier: RevenueTierType
  isLoading: boolean
}>()

const emits = defineEmits<{
  save: [clientId: number, tierId: number]
}>()

const isOpen = defineModel<boolean>('modelValue', { default: false })

const clientQuery = ref(String(props.clientId))
const { data: clients, isLoading: isClientsLoading } = useClientsList(() => ({
  query: clientQuery.value,
  size: 20,
  sortBy: 'created_at:desc',
}))

/** The selected client and its tier come from the creator record, where shares are strings. */
type ClientOption = {
  id: number
  name: string
  revenueTiers?: { id: number; name: string; revenueShare: number | string }[]
}

const stableClients = ref<ClientOption[]>(clients.value)

const seedCurrentClient = () => {
  if (!props.clientId) {
    return
  }

  const currentClient: ClientOption = {
    id: props.clientId,
    name: props.clientName,
    revenueTiers: [props.revenueTier],
  }
  const otherClients = stableClients.value.filter((client) => client.id !== props.clientId)

  stableClients.value = [currentClient, ...otherClients]
}

watch(clients, (val) => {
  if (val.length > 0) {
    stableClients.value = val
    seedCurrentClient()
  }
})

const selectedClientId = ref(props.clientId)
const selectedTierId = ref<number | undefined>(props.revenueTierId)

const { data: tierClients, isLoading: isTiersLoading } = useClientsList(() => ({
  query: selectedClientId.value ? String(selectedClientId.value) : '',
  size: 20,
}))

const getDefaultRevenueTierId = (tiers: { id: number; name: string }[]) => {
  const defaultTier = tiers.find((tier) => tier.name.toLowerCase() === 'default')

  return defaultTier?.id ?? tiers[0]?.id
}

const availableTiers = computed(() => {
  const client = tierClients.value.find((c) => c.id === selectedClientId.value)
  return client?.revenueTiers ?? []
})

const tierOptions = computed(() => {
  const options = availableTiers.value.map((tier) => ({ label: formatRevenueTierLabel(tier), value: tier.id }))
  const hasSelectedTier = options.some((option) => option.value === selectedTierId.value)

  if (!hasSelectedTier && selectedTierId.value !== undefined && selectedClientId.value === props.clientId) {
    return [{ label: formatRevenueTierLabel(props.revenueTier), value: props.revenueTier.id }, ...options]
  }

  return options
})

const standardOption = [{ label: 'Standard - 100%', value: 'standard' }]

watch(
  () => isOpen.value,
  (open) => {
    if (open) {
      selectedClientId.value = props.clientId
      selectedTierId.value = props.revenueTierId
      clientQuery.value = String(props.clientId)
      seedCurrentClient()
    }
  },
  { immediate: true },
)

const syncTierSelectionForClient = (clientId: number) => {
  if (clientId === props.clientId) {
    selectedTierId.value = props.revenueTierId
    return
  }

  if (isTiersLoading.value) {
    selectedTierId.value = undefined
    return
  }

  const tiers = availableTiers.value
  if (tiers.length > 0) {
    selectedTierId.value = getDefaultRevenueTierId(tiers)
    return
  }

  selectedTierId.value = props.revenueTierId
}

watch(selectedClientId, (newClientId) => {
  if (!isOpen.value) {
    return
  }

  syncTierSelectionForClient(newClientId)
})

watch([availableTiers, isTiersLoading], () => {
  if (!isOpen.value || selectedClientId.value === props.clientId) {
    return
  }

  syncTierSelectionForClient(selectedClientId.value)
})

const onRemoteSearch = debounce((query: string) => {
  clientQuery.value = query
}, 300)

const clientOptions = computed(() => {
  const options = stableClients.value.map((client) => ({ label: client.name, value: client.id }))

  if (props.clientId && !options.some((option) => option.value === props.clientId)) {
    return [{ label: props.clientName, value: props.clientId }, ...options]
  }

  return options
})

const isSaveDisabled = computed(() => {
  const hasChangedClient = selectedClientId.value !== props.clientId

  if (!hasChangedClient) {
    return false
  }

  if (isTiersLoading.value) {
    return true
  }

  if (tierOptions.value.length === 0) {
    return false
  }

  return selectedTierId.value === undefined
})

const onSave = () => {
  if (!isSaveDisabled.value && selectedTierId.value !== undefined) {
    emits('save', selectedClientId.value, selectedTierId.value)
  }
}
</script>
<template>
  <EditFieldModal v-model="isOpen" title="Edit client" :is-loading="isLoading" :is-save-disabled="isSaveDisabled" @save="onSave">
    <SelectComponent
      v-model="selectedClientId"
      :options="clientOptions"
      placeholder="Select client"
      filterable
      remote
      :remote-method="onRemoteSearch"
      :is-loading="isClientsLoading"
      :teleported="false"
    />
    <BoxComponent :mt="16">
      <SelectComponent v-if="isTiersLoading" model-value="" :options="[]" placeholder="Loading..." disabled />
      <SelectComponent
        v-else-if="tierOptions.length > 0"
        v-model="selectedTierId"
        :options="tierOptions"
        placeholder="Select revenue tier"
        :teleported="false"
      />
      <SelectComponent v-else model-value="standard" :options="standardOption" disabled />
    </BoxComponent>
  </EditFieldModal>
</template>
